/**
 * Asset sharing — the single data module for "send an asset into a chat".
 *
 * Everything that reads or writes an asset share goes through here: the picker
 * search, the recipient-access check the send dialog shows, the RLS-scoped read
 * behind the chat card, and the metadata a sent message carries.
 *
 * ── The one invariant ────────────────────────────────────────────────────────
 * A sent asset message stores ONLY the asset id: `{ shared_asset_id }`. No
 * title, no description, no thumbnail, no category — and no title in the
 * message `content` either, because content is visible to every participant
 * regardless of whether they can open the asset. The receiving client resolves
 * the card through its own RLS-scoped read, so a member without access gets an
 * "unavailable" card that is indistinguishable from a deleted asset and learns
 * nothing. Same model as `SharedRecordingPreview` / the feed's
 * `referenced_event_id`.
 *
 * Two legacy shapes exist in the wild and are read-only here — see
 * {@link getSharedAssetId}:
 *   A. `/asset` slash command: `{ share_type: 'asset', asset_id, asset_title, … }`
 *   B. ShareItemDialog:        `{ shared_asset_id, shared_asset_title, … }`
 * Both snapshot the title into metadata, so old messages still leak it. Nothing
 * new writes either shape.
 */

import {
  keepPreviousData,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { supabase } from '@/lib/supabase';
import {
  LIBRARY_ASSET_SELECT,
  transformLibraryAsset,
  type LibraryAsset,
} from '@/api/client';
import { useSendMessage } from '@/api/community-hooks';
import { sendMessage } from '@/api/community';
import type { MessageWithContext, SendMessagePayload } from '@/api/community';
import type { Asset } from '@/components/assets/AssetCard';

// ===========================================
// Query keys
// ===========================================

/**
 * Keyed under a single `assetShare` root so one `invalidateQueries({ queryKey:
 * assetShareKeys.all })` clears every read in this module.
 */
export const assetShareKeys = {
  all: ['assetShare'] as const,
  asset: (assetId: string) => ['assetShare', 'asset', assetId] as const,
  search: (params: AssetSearchParams) =>
    [
      'assetShare',
      'search',
      {
        query: params.query?.trim() ?? '',
        kind: params.kind ?? null,
        limit: params.limit ?? DEFAULT_SEARCH_LIMIT,
      },
    ] as const,
  access: (params: AssetAccessParams) =>
    [
      'assetShare',
      'access',
      {
        assetIds: normaliseIds(params.assetIds),
        userIds: normaliseIds(params.userIds),
        conversationIds: normaliseIds(params.conversationIds),
      },
    ] as const,
};

/** Sorted + deduped so two callers asking for the same set share one cache entry. */
function normaliseIds(ids: readonly string[] | undefined): string[] {
  if (!ids || ids.length === 0) return [];
  return [...new Set(ids.filter((id): id is string => typeof id === 'string' && id.length > 0))].sort();
}

// ===========================================
// Message metadata
// ===========================================

/** Metadata written by every new asset send. Deliberately id-only. */
export interface AssetShareMetadata {
  shared_asset_id: string;
  /** Index signature only so this satisfies the `Record<string, unknown>`
   *  metadata slot on SendMessagePayload / shareableItemMetadata. Nothing but
   *  `shared_asset_id` is ever written — see the file header. */
  [key: string]: unknown;
}

/**
 * The only place a new asset-share message shape is constructed.
 *
 * Callers must not add title/description/thumbnail keys — see the file header.
 */
export function buildAssetShareMetadata(assetId: string): AssetShareMetadata {
  return { shared_asset_id: assetId };
}

/**
 * Read the shared asset id out of ANY of the three message-metadata shapes:
 * the current `{ shared_asset_id }`, legacy B (same key plus a title snapshot)
 * and legacy A (`{ share_type: 'asset', asset_id }`).
 *
 * Returns null for anything else — junk, a course share, a non-string id.
 */
export function getSharedAssetId(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const meta = metadata as Record<string, unknown>;

  // Current shape + legacy B (ShareItemDialog).
  const shared = meta.shared_asset_id;
  if (typeof shared === 'string' && shared.trim().length > 0) return shared.trim();

  // Legacy A (`/asset` slash command). Gated on share_type so a course or
  // recording share can never be mistaken for an asset.
  if (meta.share_type === 'asset') {
    const legacy = meta.asset_id;
    if (typeof legacy === 'string' && legacy.trim().length > 0) return legacy.trim();
  }

  return null;
}

// ===========================================
// One asset, through the viewer's own RLS
// ===========================================

/**
 * Read one asset as the current viewer.
 *
 * Returns null when the row is not visible — no access, or deleted. The caller
 * MUST render both cases identically (see `AssetShareCard`), otherwise the
 * distinction leaks whether the asset exists.
 */
export async function fetchSharedAsset(assetId: string): Promise<Asset | null> {
  if (!assetId) return null;
  const { data, error } = await supabase
    .from('assets')
    .select(LIBRARY_ASSET_SELECT)
    .eq('id', assetId)
    .maybeSingle();

  // An RLS denial comes back as an empty result, not an error, but a genuine
  // transport/permission error is also "can't show it" from the card's point of
  // view — never surface the difference.
  if (error || !data) return null;
  return transformLibraryAsset(data as unknown as LibraryAsset);
}

export function useSharedAsset(assetId: string | null | undefined): UseQueryResult<Asset | null> {
  return useQuery({
    queryKey: assetShareKeys.asset(assetId ?? ''),
    queryFn: () => fetchSharedAsset(assetId as string),
    enabled: !!assetId,
    // Assets are ClickUp-synced reference data: a chat thread can hold dozens of
    // cards and none of them need to be fresher than the library list itself.
    staleTime: 5 * 60 * 1000,
    // An unreadable asset stays unreadable; retrying just costs round-trips.
    retry: false,
  });
}

// ===========================================
// Search (picker + Library search share one engine)
// ===========================================

export const DEFAULT_SEARCH_LIMIT = 20;

/** `p_kind` values accepted by `search_assets`. */
export type AssetSearchKind = 'template' | 'sop' | 'policy' | 'checklist' | 'video';

/** Which bucket the row came from when the query is empty, or `match` when it isn't. */
export type AssetSearchSection = 'recent' | 'organisation' | 'popular' | 'match';

/** Which field produced the hit — drives the "matched in description" hint. */
export type AssetSearchMatchField = 'title' | 'description' | 'tags' | 'category' | null;

export interface AssetSearchParams {
  query?: string;
  kind?: AssetSearchKind | null;
  limit?: number;
}

export interface AssetSearchResult {
  /** Mapped through the shared library mapper — same shape the Library renders. */
  asset: Asset;
  section: AssetSearchSection;
  matchField: AssetSearchMatchField;
  /** When this viewer last sent this asset, or null. */
  lastSentAt: string | null;
  /** Display name of who it was last sent to, or null. */
  lastSentTo: string | null;
  rank: number;
}

/**
 * Row → `Asset` mapping for `search_assets`, isolated on purpose.
 *
 * `search_assets` is built in hermes-alfie in parallel with this module; if its
 * exact column names move, THIS is the only function to adjust. It accepts the
 * joined media either as a nested `media` object (what `LIBRARY_ASSET_SELECT`
 * produces) or as flat `media_*` columns (what a SQL function returning a table
 * naturally produces), so either shape lands correctly.
 */
export function mapAssetSearchRow(row: Record<string, unknown>): AssetSearchResult {
  return {
    asset: transformLibraryAsset(toLibraryAssetRow(row)),
    section: (row.section as AssetSearchSection) ?? 'match',
    matchField: (row.match_field as AssetSearchMatchField) ?? null,
    lastSentAt: (row.last_sent_at as string | null) ?? null,
    lastSentTo: (row.last_sent_to as string | null) ?? null,
    rank: typeof row.rank === 'number' ? row.rank : 0,
  };
}

function toLibraryAssetRow(row: Record<string, unknown>): LibraryAsset {
  const nested = row.media;
  const media =
    nested && typeof nested === 'object'
      ? (nested as LibraryAsset['media'])
      : mediaFromFlatColumns(row);
  return { ...row, media } as unknown as LibraryAsset;
}

function mediaFromFlatColumns(row: Record<string, unknown>): LibraryAsset['media'] {
  if (!('media_id' in row) && !('media_status' in row)) return null;
  return {
    id: (row.media_id as string) ?? '',
    status: (row.media_status as string | null) ?? null,
    playback_url: (row.media_playback_url as string | null) ?? null,
    poster_url: (row.media_poster_url as string | null) ?? null,
    small_poster_url: (row.media_small_poster_url as string | null) ?? null,
    thumbnail_url: (row.media_thumbnail_url as string | null) ?? null,
    blurhash: (row.media_blurhash as string | null) ?? null,
    duration_seconds: (row.media_duration_seconds as number | null) ?? null,
    original_size_bytes: (row.media_original_size_bytes as number | null) ?? null,
    original_mime_type: (row.media_original_mime_type as string | null) ?? null,
    metadata: row.media_metadata ?? null,
  };
}

/**
 * Server-side asset search. Empty query returns the default sections
 * (recent / organisation / popular); a non-empty query returns ranked matches.
 *
 * The caller debounces — this fires on every call.
 */
export async function searchAssets(params: AssetSearchParams = {}): Promise<AssetSearchResult[]> {
  const { data, error } = await supabase.rpc('search_assets', {
    p_query: params.query?.trim() ?? '',
    p_kind: params.kind ?? null,
    p_limit: params.limit ?? DEFAULT_SEARCH_LIMIT,
  });
  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(mapAssetSearchRow);
}

export function useAssetSearch(
  params: AssetSearchParams & { enabled?: boolean } = {},
): UseQueryResult<AssetSearchResult[]> {
  const { enabled = true, ...search } = params;
  return useQuery({
    queryKey: assetShareKeys.search(search),
    queryFn: () => searchAssets(search),
    enabled,
    // The picker retypes constantly: hold the previous results in place so the
    // list doesn't blank out between keystrokes.
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

// ===========================================
// Recipient access
// ===========================================

export type AssetAccessTargetType = 'user' | 'conversation';

export interface AssetAccessRow {
  assetId: string;
  targetType: AssetAccessTargetType;
  targetId: string;
  /** People in that target (1 for a user, the participant count for a conversation). */
  memberCount: number;
  /** How many of them cannot open the asset. */
  blockedCount: number;
}

export interface AssetAccessCount {
  memberCount: number;
  blockedCount: number;
}

export interface AssetAccessParams {
  assetIds: readonly string[];
  userIds?: readonly string[];
  conversationIds?: readonly string[];
}

/**
 * Who among a set of targets can open a set of assets.
 *
 * Assets the CALLER cannot open and conversations the caller is not in are
 * omitted by the function, so a missing row means "unknown", never "everyone
 * is blocked" — `get` returns undefined and callers must show nothing rather
 * than guess.
 */
export async function fetchAssetAccess(params: AssetAccessParams): Promise<AssetAccessRow[]> {
  const assetIds = normaliseIds(params.assetIds);
  const userIds = normaliseIds(params.userIds);
  const conversationIds = normaliseIds(params.conversationIds);
  if (assetIds.length === 0 || (userIds.length === 0 && conversationIds.length === 0)) {
    return [];
  }

  const { data, error } = await supabase.rpc('asset_access_for_targets', {
    p_asset_ids: assetIds,
    p_user_ids: userIds,
    p_conversation_ids: conversationIds,
  });
  if (error) throw error;

  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    assetId: String(row.asset_id),
    targetType: row.target_type as AssetAccessTargetType,
    targetId: String(row.target_id),
    memberCount: Number(row.member_count ?? 0),
    blockedCount: Number(row.blocked_count ?? 0),
  }));
}

export interface UseAssetAccessResult {
  rows: AssetAccessRow[];
  /** undefined = no row for that target, i.e. unknown. Never treat as zero. */
  get: (
    assetId: string,
    targetType: AssetAccessTargetType,
    targetId: string,
  ) => AssetAccessCount | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

export function useAssetAccess(
  params: AssetAccessParams & { enabled?: boolean },
): UseAssetAccessResult {
  const { enabled = true, ...access } = params;
  const assetIds = normaliseIds(access.assetIds);
  const userIds = normaliseIds(access.userIds);
  const conversationIds = normaliseIds(access.conversationIds);
  const hasTargets = assetIds.length > 0 && (userIds.length > 0 || conversationIds.length > 0);

  const query = useQuery({
    queryKey: assetShareKeys.access({ assetIds, userIds, conversationIds }),
    queryFn: () => fetchAssetAccess({ assetIds, userIds, conversationIds }),
    enabled: enabled && hasTargets,
    // A chat thread can hold many cards; each one asks for its own conversation.
    // Hold the answer long enough that scrolling back never re-asks.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const rows = query.data ?? EMPTY_ACCESS_ROWS;

  const index = useMemo(() => {
    const map = new Map<string, AssetAccessCount>();
    for (const row of rows) {
      map.set(accessKey(row.assetId, row.targetType, row.targetId), {
        memberCount: row.memberCount,
        blockedCount: row.blockedCount,
      });
    }
    return map;
  }, [rows]);

  const get = useMemo(
    () =>
      (assetId: string, targetType: AssetAccessTargetType, targetId: string) =>
        index.get(accessKey(assetId, targetType, targetId)),
    [index],
  );

  return {
    rows,
    get,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
}

/** Stable identity so `rows` doesn't change every render before data lands. */
const EMPTY_ACCESS_ROWS: AssetAccessRow[] = [];

function accessKey(assetId: string, targetType: AssetAccessTargetType, targetId: string): string {
  return `${assetId}|${targetType}|${targetId}`;
}

// ===========================================
// Sending
// ===========================================

export interface SendAssetMessageArgs {
  conversationId: string;
  assetId: string;
  /** The sender's own words. Never a generated "Shared an asset: <title>" line —
   *  content is readable by every participant, including those without access. */
  text?: string;
  replyToId?: string;
  replyTo?: { id: string; content: string; sender_name: string } | null;
}

/**
 * The payload every asset send path builds. Content may be empty — the
 * `sendMessagePayloadSchema` empty-content allowlist accepts a message whose
 * metadata carries `shared_asset_id`.
 */
export function buildAssetMessagePayload(
  args: Pick<SendAssetMessageArgs, 'assetId' | 'text' | 'replyToId'>,
): SendMessagePayload {
  return {
    content: args.text?.trim() ?? '',
    messageType: 'text',
    replyToId: args.replyToId,
    metadata: buildAssetShareMetadata(args.assetId),
    clientTempId: crypto.randomUUID(),
  };
}

export interface UseSendAssetMessageResult {
  /** Fire-and-forget (optimistic insert + failure state) — chat composers. */
  sendAssetMessage: (args: SendAssetMessageArgs) => void;
  /** Awaitable — fan-out senders like the share dialog. */
  sendAssetMessageAsync: (args: SendAssetMessageArgs) => Promise<MessageWithContext>;
  isPending: boolean;
  mutation: UseMutationResult<
    MessageWithContext,
    Error,
    { conversationId: string; payload: SendMessagePayload; replyTo?: { id: string; content: string; sender_name: string } | null },
    { messageId: string }
  >;
}

/**
 * The single asset send path. Wraps `useSendMessage` so optimistic insert,
 * failed-send retry and cache reconciliation stay exactly as they are for every
 * other message type.
 */
export function useSendAssetMessage(): UseSendAssetMessageResult {
  const mutation = useSendMessage();

  const helpers = useMemo(
    () => ({
      sendAssetMessage: (args: SendAssetMessageArgs) => {
        mutation.mutate({
          conversationId: args.conversationId,
          payload: buildAssetMessagePayload(args),
          replyTo: args.replyTo,
        });
      },
      sendAssetMessageAsync: (args: SendAssetMessageArgs) =>
        mutation.mutateAsync({
          conversationId: args.conversationId,
          payload: buildAssetMessagePayload(args),
          replyTo: args.replyTo,
        }),
    }),
    [mutation],
  );

  return { ...helpers, isPending: mutation.isPending, mutation };
}

/**
 * Non-hook send, for code that already holds a conversation id and does not
 * need the optimistic cache update (non-React callers, tests).
 */
export async function sendAssetMessage(args: SendAssetMessageArgs): Promise<MessageWithContext> {
  return sendMessage(args.conversationId, buildAssetMessagePayload(args));
}

/**
 * The one auto-generated body the legacy `/asset` slash command ever wrote.
 *
 * Both legacy send paths did the identical thing —
 *   ChatView.tsx:        content: text || `Shared an asset: ${attachment.data.title}`
 *   GroupDetailView.tsx: content: text || `Shared an asset: ${attachment.data.title}`
 * — so a share the sender typed nothing into still carries the asset TITLE in
 * `content`. Content is rendered to every participant, including the ones whose
 * card resolves to "Asset unavailable", which is exactly the leak the id-only
 * metadata shape was introduced to close (this module).
 *
 * Verified across the full git history (1760 commits): this is the ONLY
 * generated form. The other legacy path, ShareItemDialog, has always sent
 * `introMessage.trim()` and nothing else, so it needs no second pattern.
 */
export const LEGACY_ASSET_CONTENT_PREFIX = 'Shared an asset: ';

/**
 * True only for a message that is BOTH an asset share and carries that exact
 * generated opener. Deliberately narrow on both halves: a non-asset message is
 * never touched, and real typed words are never hidden — the sender's own text
 * replaced the fallback entirely (`text || …`), so a message that starts with
 * the prefix cannot also contain something they wrote.
 */
export function isLegacyAssetShareContent(content: unknown, metadata: unknown): boolean {
  if (!getSharedAssetId(metadata)) return false;
  return typeof content === 'string' && content.startsWith(LEGACY_ASSET_CONTENT_PREFIX);
}
